import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { 
  Loader2, Plus, MessageSquare, ThumbsUp, Flag, Clock, User, 
  ChevronLeft, Lock, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  GENERAL_DISCUSSION: "General Discussion",
  FARRIER_TALK: "Farrier Talk",
  TRAINING_BEHAVIOR: "Training & Behavior",
  LIVESTOCK: "Livestock",
  WORKING_DOGS: "Working Dogs",
  EQUIPMENT_TOOLS: "Equipment & Tools",
  BUSINESS_PRICING: "Business & Pricing",
  RANCH_SERVICES: "Ranch Services"
};

const CATEGORY_COLORS = {
  GENERAL_DISCUSSION: "bg-gray-100 text-gray-800",
  FARRIER_TALK: "bg-amber-100 text-amber-800",
  TRAINING_BEHAVIOR: "bg-blue-100 text-blue-800",
  LIVESTOCK: "bg-green-100 text-green-800",
  WORKING_DOGS: "bg-purple-100 text-purple-800",
  EQUIPMENT_TOOLS: "bg-orange-100 text-orange-800",
  BUSINESS_PRICING: "bg-indigo-100 text-indigo-800",
  RANCH_SERVICES: "bg-teal-100 text-teal-800"
};

export default function LoungeDiscussions() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', category: '', body: '' });
  const [creating, setCreating] = useState(false);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    loadCategories();
    loadPosts();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lounge/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPosts = async () => {
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/api/lounge/posts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  };

  const loadPost = async (postId) => {
    try {
      const response = await axios.get(`${API_URL}/api/lounge/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data.post);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Error loading post:', error);
      toast.error('Failed to load discussion');
    }
  };

  const createPost = async () => {
    if (!newPost.title || !newPost.category || !newPost.body) {
      toast.error('Please fill in all fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/lounge/posts`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discussion created!');
      setShowNewPost(false);
      setNewPost({ title: '', category: '', body: '' });
      loadPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discussion');
    } finally {
      setCreating(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API_URL}/api/lounge/posts/${selectedPost.id}/comments`, 
        { body: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewComment('');
      loadPost(selectedPost.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    }
  };

  const markHelpful = async (type, id) => {
    try {
      await axios.post(`${API_URL}/api/lounge/helpful/${type}/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedPost) {
        loadPost(selectedPost.id);
      } else {
        loadPosts();
      }
    } catch (error) {
      toast.error('Failed to mark as helpful');
    }
  };

  const report = async (type, id) => {
    if (!window.confirm('Are you sure you want to report this content?')) return;
    try {
      await axios.post(`${API_URL}/api/lounge/report/${type}/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reported. Thank you for helping keep the community safe.');
      if (selectedPost) {
        loadPost(selectedPost.id);
      } else {
        loadPosts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Post Detail View
  if (selectedPost) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setSelectedPost(null)}>
          <ChevronLeft className="h-4 w-4 mr-2" /> Back to Discussions
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={CATEGORY_COLORS[selectedPost.category]}>
                    {CATEGORY_LABELS[selectedPost.category]}
                  </Badge>
                  {selectedPost.status === 'LOCKED' && (
                    <Badge variant="outline" className="text-yellow-600">
                      <Lock className="h-3 w-3 mr-1" /> Locked
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-xl">{selectedPost.title}</CardTitle>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                  <User className="h-4 w-4" />
                  <span>{selectedPost.providerName} • {selectedPost.businessName}</span>
                  <span>•</span>
                  <Clock className="h-4 w-4" />
                  <span>{formatDate(selectedPost.createdAt)}</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap mb-4">{selectedPost.body}</p>
            <div className="flex items-center gap-4 pt-4 border-t">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => markHelpful('post', selectedPost.id)}
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                Helpful ({selectedPost.helpfulCount})
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => report('post', selectedPost.id)}
              >
                <Flag className="h-4 w-4 mr-1" />
                Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Comments */}
        <div className="space-y-4">
          <h3 className="font-semibold">{comments.length} Comments</h3>
          
          {selectedPost.status !== 'LOCKED' && (
            <Card>
              <CardContent className="pt-4">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                />
                <Button className="mt-2" onClick={addComment} disabled={!newComment.trim()}>
                  Post Comment
                </Button>
              </CardContent>
            </Card>
          )}

          {comments.filter(c => c.status !== 'HIDDEN').map((comment) => (
            <Card key={comment.id}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <User className="h-4 w-4" />
                  <span className="font-medium text-foreground">{comment.providerName}</span>
                  <span>• {comment.businessName}</span>
                  <span>• {formatDate(comment.createdAt)}</span>
                </div>
                <p className="whitespace-pre-wrap">{comment.body}</p>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => markHelpful('comment', comment.id)}
                  >
                    <ThumbsUp className="h-4 w-4 mr-1" />
                    ({comment.helpfulCount})
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => report('comment', comment.id)}
                  >
                    <Flag className="h-4 w-4 mr-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Posts List View
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Dialog open={showNewPost} onOpenChange={setShowNewPost}>
          <DialogTrigger asChild>
            <Button onClick={() => setShowRules(true)}>
              <Plus className="h-4 w-4 mr-2" /> New Discussion
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Start a Discussion</DialogTitle>
            </DialogHeader>
            
            {showRules && (
              <Alert className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Community Guidelines:</strong> No client names, addresses, phone numbers, 
                  threats, illegal content, or spam in Lounge discussions. Use Provider Alerts 
                  for specific client warnings.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={newPost.category} 
                  onValueChange={(v) => setNewPost({...newPost, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  placeholder="What's your topic?"
                  maxLength={200}
                />
              </div>
              <div>
                <Label>Content</Label>
                <Textarea
                  value={newPost.body}
                  onChange={(e) => setNewPost({...newPost, body: e.target.value})}
                  placeholder="Share your thoughts, questions, or advice..."
                  rows={6}
                  maxLength={10000}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewPost(false)}>Cancel</Button>
              <Button onClick={createPost} disabled={creating}>
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Post Discussion
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No discussions yet. Be the first to start one!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.filter(p => p.status !== 'HIDDEN').map((post) => (
            <Card 
              key={post.id} 
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => loadPost(post.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={CATEGORY_COLORS[post.category]} variant="secondary">
                        {CATEGORY_LABELS[post.category]}
                      </Badge>
                      {post.status === 'LOCKED' && (
                        <Lock className="h-4 w-4 text-yellow-600" />
                      )}
                    </div>
                    <h3 className="font-semibold line-clamp-1">{post.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {post.body}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span>{post.providerName}</span>
                      <span>•</span>
                      <span>{formatDate(post.createdAt)}</span>
                      <span>•</span>
                      <span className="flex items-center">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        {post.commentCount}
                      </span>
                      <span className="flex items-center">
                        <ThumbsUp className="h-3 w-3 mr-1" />
                        {post.helpfulCount}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
